import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ReviewCard, ReviewsAggregate } from "@/components/ReaderReviews";
import BrandWordmark from "@/components/BrandWordmark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = "#69d2c7";

export default function Reviews() {
  const [data, setData] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        if (alive) setData(res.data);
      })
      .catch(() => {
        if (alive) setData({ reviews: [], average_rating: 0, total_count: 0 });
      });
    return () => {
      alive = false;
    };
  }, []);

  const loaded = data !== null;
  const total = loaded ? data.total_count : 0;
  const avg = loaded ? data.average_rating : 0;
  const shown = loaded ? data.reviews : [];

  return (
    <div
      className="min-h-screen bg-white text-neutral-950 flex flex-col"
      style={{ fontFamily: "'Inter Tight', ui-sans-serif, system-ui, sans-serif" }}
      data-testid="page-reviews"
    >
      <main className="flex-1">
        <section className="max-w-screen-lg mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-12 md:pb-16 text-center">
          <span className="inline-flex items-center gap-1" data-testid="reviews-brand-static">
            <span className="font-display text-lg md:text-xl leading-none tracking-tight text-ink" style={{ fontWeight: 800 }}>
              The Biology Trader
            </span>
            <span className="text-[10px] align-top text-[#245943] ml-0.5 mt-[-4px]" aria-hidden="true">™</span>
          </span>
          <div className="mt-5">
            <Link
              to="/free-book"
              className="text-sm font-semibold underline underline-offset-4 decoration-2 hover:text-neutral-600"
              style={{ textDecorationColor: TEAL }}
              data-testid="reviews-back-to-book"
            >
              ← Get the book
            </Link>
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight" data-testid="reviews-page-title">
            Reader reviews
          </h1>
          <p
            className="mt-4 text-base sm:text-lg leading-relaxed text-neutral-600 max-w-lg mx-auto"
            style={{ textWrap: "balance" }}
            data-testid="reviews-page-supporting"
          >
            Read honest feedback from traders who have read THE CAGE, or add your own below.
          </p>
          <div className="mt-7">
            <Link
              to="/leave"
              className="inline-block text-base font-semibold py-3.5 px-8 bg-neutral-950 text-white hover:bg-neutral-800 transition-colors"
              data-testid="reviews-leave-review-cta"
            >
              Leave a review
            </Link>
          </div>

          {loaded && total >= 5 && <ReviewsAggregate avg={avg} total={total} className="mt-7" />}

          {loaded && total === 0 && (
            <p className="mt-10 text-base text-neutral-600" data-testid="reviews-page-empty-state">
              No reviews yet. Read the book, then be the first.
            </p>
          )}
        </section>

        <section className="border-t border-neutral-200" data-testid="reviews-list-section">
          <div className="max-w-screen-lg mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
            {shown.length > 0 && (
              <div
                className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6"
                data-testid="reviews-page-grid"
              >
                {shown.map((r, i) => (
                  <ReviewCard key={i} r={r} i={i} showDate />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200">
        <div className="max-w-screen-md mx-auto px-5 md:px-8 py-8 text-sm text-neutral-500">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-3">
              <BrandWordmark testId="reviews-footer-brand-link" />
              <span>© {new Date().getFullYear()} Biology Trader Education LLC</span>
            </span>
            <span className="flex items-center gap-5">
              <Link to="/risk-disclosure" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-950 underline underline-offset-4" data-testid="reviews-risk-link">
                Risk Disclosure
              </Link>
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-neutral-950 underline underline-offset-4" data-testid="reviews-privacy-link">
                Privacy Policy
              </Link>
            </span>
          </div>
          <p className="block mt-4 text-xs text-neutral-400" data-testid="reviews-risk-line">
            Trading involves substantial risk. Past performance does not guarantee future results.
          </p>
        </div>
      </footer>
    </div>
  );
}
